import { Model } from 'app/model/backendmodel';
import { AdapterRegistry } from '../../../adapter-registry';
import { Metamodel } from 'app/model/base/model';
import { CopySink } from 'app/model/pipeline/copysink/copysink';
import { FormatWriteSettingsAdapter, formatWriteSettingsAdapts } from './format-write-settings.adapter';
import { DeltaFormatWriteSettings } from 'app/model/pipeline/copysink/format-write-settings/delta-format-write-settings';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { Owned, StoreSettingPartitionKey } from 'shell-models-lib';
import { arrayIsNullOrEmpty } from 'app/registries/utls/util';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(DeltaFormatWriteSettings),
    asynchronous: true,
    adapts: {
        ...formatWriteSettingsAdapts,
        writeBehavior: '*',
        partitionOption: '*',
        partitionNameList: '*',
        upsertSettings: '*',
        applyVOrder: '*'
    }
})
export class DeltaFormatWriteSettingsAdapter extends FormatWriteSettingsAdapter<
    DeltaFormatWriteSettings,
    Model.DeltaFormatWriteSettings
> {
    protected override async _toResource(model: DeltaFormatWriteSettings): Promise<Model.DeltaFormatWriteSettings> {
        const result: Model.DeltaFormatWriteSettings = FormatWriteSettingsAdapter.getCommonWriteSettingsProperties(
            Metamodel.modelFor(DeltaFormatWriteSettings).name
        );
        FormatWriteSettingsAdapter.toFormatWriteSettingsResource(model, result);

        // Write behavior
        result.writeBehavior = ValueAdapter.toResource(model, DeltaFormatWriteSettings.WRITE_BEHAVIOR);

        if (model.writeBehavior === 'Insert') {
            this._toPartitionResource(
                result,
                model,
                DeltaFormatWriteSettings.APPEND_PARTITION_OPTION,
                model.enableAppendPartitionOption,
                DeltaFormatWriteSettings.APPEND_PARTITION_KEYS,
                model.appendPartitionNameList
            );
        } else if (model.writeBehavior === 'Upsert') {
            this._toPartitionResource(
                result,
                model,
                DeltaFormatWriteSettings.UPSERT_PARTITION_OPTION,
                model.enableUpsertPartitionOption,
                DeltaFormatWriteSettings.UPSERT_PARTITION_KEYS,
                model.upsertPartitionNameList
            );
        } else {
            this._toPartitionResource(
                result,
                model,
                DeltaFormatWriteSettings.PARTITION_OPTION,
                model.enablePartitionOption,
                DeltaFormatWriteSettings.PARTITION_KEYS,
                model.partitionNameList
            );
        }

        // Upsert settings (key columns)
        if (model.writeBehavior === 'Upsert' || ValueAdapter.isTemplated(model, DeltaFormatWriteSettings.WRITE_BEHAVIOR)) {
            result.upsertSettings = {
                keys: ValueAdapter.toResource(model, DeltaFormatWriteSettings.UPSERT_KEYS)
            };
        }

        // Apply V-Order
        result.applyVOrder = ValueAdapter.toResource(model, DeltaFormatWriteSettings.APPLY_VORDER);

        return result;
    }

    protected override async _toModel(
        resource: Model.DeltaFormatWriteSettings,
        copySink: CopySink
    ): Promise<DeltaFormatWriteSettings> {
        const result = FormatWriteSettingsAdapter.createWriteSettingsWithCommonProperties(
            DeltaFormatWriteSettings.MODEL,
            copySink
        ) as DeltaFormatWriteSettings;
        FormatWriteSettingsAdapter.toFormatWriteSettingsModel(resource, result);

        // Write behavior
        ValueAdapter.toModel(result, DeltaFormatWriteSettings.WRITE_BEHAVIOR, resource.writeBehavior);

        if (resource.writeBehavior === 'Insert') {
            this._toPartitionModel(
                resource,
                result,
                DeltaFormatWriteSettings.APPEND_PARTITION_OPTION,
                'enableAppendPartitionOption',
                DeltaFormatWriteSettings.APPEND_PARTITION_KEYS,
                'appendPartitionNameList'
            );
            result.enablePartitionOption = null;
            result.partitionNameList = null;
            result.enableUpsertPartitionOption = null;
            result.upsertPartitionNameList = null;
        } else if (resource.writeBehavior === 'Upsert') {
            this._toPartitionModel(
                resource,
                result,
                DeltaFormatWriteSettings.UPSERT_PARTITION_OPTION,
                'enableUpsertPartitionOption',
                DeltaFormatWriteSettings.UPSERT_PARTITION_KEYS,
                'upsertPartitionNameList'
            );
            result.enablePartitionOption = null;
            result.partitionNameList = null;
            result.enableAppendPartitionOption = null;
            result.appendPartitionNameList = null;
        } else {
            this._toPartitionModel(
                resource,
                result,
                DeltaFormatWriteSettings.PARTITION_OPTION,
                'enablePartitionOption',
                DeltaFormatWriteSettings.PARTITION_KEYS,
                'partitionNameList'
            );

            if (typeof resource.writeBehavior === 'string') {
                result.enableAppendPartitionOption = null;
                result.appendPartitionNameList = null;
                result.enableUpsertPartitionOption = null;
                result.upsertPartitionNameList = null;
            }
        }

        // Upsert settings (key columns)
        if (resource.upsertSettings) {
            ValueAdapter.toModel(result, DeltaFormatWriteSettings.UPSERT_KEYS, resource.upsertSettings.keys);
        }

        // Apply V-Order
        if (resource.applyVOrder != null) {
            ValueAdapter.toModel(result, DeltaFormatWriteSettings.APPLY_VORDER, resource.applyVOrder);
        }

        return result;
    }

    private _toPartitionResource(
        result: Model.DeltaFormatWriteSettings,
        model: DeltaFormatWriteSettings,
        optionAttribute: typeof DeltaFormatWriteSettings.PARTITION_OPTION,
        optionValue: boolean | null | undefined,
        keysAttribute: typeof DeltaFormatWriteSettings.PARTITION_KEYS,
        partitionNameList: StoreSettingPartitionKey[] | null | undefined
    ): void {
        if (ValueAdapter.isTemplated(model, optionAttribute)) {
            result.partitionOption = ValueAdapter.toResource(model, optionAttribute);
        } else {
            result.partitionOption =
                optionValue === true
                    ? 'PartitionByKey'
                    : optionValue === false
                    ? 'None'
                    : undefined;
        }

        if (result.partitionOption && result.partitionOption !== 'None') {
            if (ValueAdapter.isTemplated(model, keysAttribute)) {
                result.partitionNameList = ValueAdapter.toResource(model, keysAttribute);
            } else if (!arrayIsNullOrEmpty(partitionNameList)) {
                result.partitionNameList = [];
                partitionNameList.forEach((partitionName) => {
                    if (partitionName.partitionKey) {
                        (result.partitionNameList as string[]).push(partitionName.partitionKey);
                    }
                });
            }
        }
    }

    private _toPartitionModel(
        resource: Model.DeltaFormatWriteSettings,
        result: DeltaFormatWriteSettings,
        optionAttribute: typeof DeltaFormatWriteSettings.PARTITION_OPTION,
        optionProperty: 'enablePartitionOption' | 'enableAppendPartitionOption' | 'enableUpsertPartitionOption',
        keysAttribute: typeof DeltaFormatWriteSettings.PARTITION_KEYS,
        keysProperty: 'partitionNameList' | 'appendPartitionNameList' | 'upsertPartitionNameList'
    ): void {
        if (resource.partitionOption == null) {
            result[optionProperty] = null;
        } else if (resource.partitionOption === 'None') {
            result[optionProperty] = false;
        } else if (resource.partitionOption === 'PartitionByKey') {
            result[optionProperty] = true;
        } else {
            ValueAdapter.toModel(result, optionAttribute, resource.partitionOption);
        }

        ValueAdapter.toModel(result, keysAttribute, resource.partitionNameList);
        if (Array.isArray(resource.partitionNameList) && !arrayIsNullOrEmpty(resource.partitionNameList)) {
            result[keysProperty] = [];
            (resource.partitionNameList as string[]).forEach((partitionName: string) => {
                const modelPartitionColumn = Owned.create<StoreSettingPartitionKey>(
                    StoreSettingPartitionKey.MODEL,
                    result
                );
                modelPartitionColumn.partitionKey = partitionName;
                result[keysProperty].push(modelPartitionColumn);
            });
        }
    }
}
